import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Users, MessageCircle, Briefcase, Award, GraduationCap, Building2 } from 'lucide-react';
import api from '@/utils/api';
import SEO from '@/components/SEO';

const About = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await api.get('/cms/pages/about');
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { icon: Users, value: '2.15 Lac+', label: 'Employment Opportunity' },
    { icon: MessageCircle, value: '1400+', label: 'WhatsApp Groups' },
    { icon: Award, value: '70+', label: 'Succeed Entrepreneurs' },
    { icon: Briefcase, value: '13+', label: 'Job Fairs' },
    { icon: GraduationCap, value: '201+', label: 'Career Guidance Programs' },
    { icon: Building2, value: '1+', label: 'Job Facilitation Center' },
  ];

  const defaultAssociations = [
    'Ministry Of Labour and Employment',
    'National Career Service',
    'National Skill Development Corporation',
    'Entrepreneurship Development Innovation Institute',
    'MSME – Micro Scale Medium Industries Department',
    'State / National Level HR Forums – NHRD',
    'Tamilnadu Skill Development Corporation',
    'Tamilnadu Education Department',
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-slate-50 to-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-slate-50 to-white flex flex-col">
      <SEO 
        title="About Us | VVVSI - Vallalar Velai Vaaipu Sevai Iyakkam"
        description="Learn about VVVSI - Vallalar Velai Vaaipu Sevai Iyakkam. A non-profit trust providing free employment services, career guidance, job fairs, and skill development across Tamil Nadu and India."
        keywords="VVVSI about, Vallalar Velai Vaaipu Sevai Iyakkam, non-profit trust, employment services, career guidance, Tamil Nadu, job placement"
        url="/about"
      />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              {content?.title || 'About Vallalar Velai Vaaipu Sevai Iyakkam'}
            </h1>
            <p className="text-xl text-teal-100 max-w-3xl">
              {content?.subtitle || 'Empowering Society Through Employment'}
            </p>
          </div>
        </div>

        {/* Quick Nav Tab Bar */}
        <div className="bg-emerald-50/60 border-y border-emerald-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
              <span className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mr-2 hidden sm:inline">Explore:</span>
              <button
                data-testid="about-tab-about"
                onClick={() => navigate('/about')}
                className="px-4 py-1.5 rounded-full text-sm font-medium bg-teal-600 text-white shadow-sm"
              >
                About Us
              </button>
              <button
                data-testid="about-tab-achievements"
                onClick={() => navigate('/achievements')}
                className="px-4 py-1.5 rounded-full text-sm font-medium bg-white text-slate-700 border border-emerald-200 hover:bg-emerald-100 hover:text-emerald-800 transition"
              >
                Achievements
              </button>
              <button
                data-testid="about-tab-clients"
                onClick={() => navigate('/our-clients')}
                className="px-4 py-1.5 rounded-full text-sm font-medium bg-white text-slate-700 border border-emerald-200 hover:bg-emerald-100 hover:text-emerald-800 transition"
              >
                Our Clients
              </button>
              <button
                data-testid="about-tab-team"
                onClick={() => navigate('/team')}
                className="px-4 py-1.5 rounded-full text-sm font-medium bg-white text-slate-700 border border-emerald-200 hover:bg-emerald-100 hover:text-emerald-800 transition"
              >
                Our Team
              </button>
              <button
                data-testid="about-tab-services"
                onClick={() => navigate('/job-facilitation-center')}
                className="px-4 py-1.5 rounded-full text-sm font-medium bg-white text-slate-700 border border-emerald-200 hover:bg-emerald-100 hover:text-emerald-800 transition"
              >
                Our Services
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-12 mb-16 items-stretch">
            <div className="flex flex-col justify-center">
              {content?.sections?.[0] ? (
                <>
                  <h2 className="text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
                    {content.sections[0].title}
                  </h2>
                  {content.sections[0].content.split('\n\n').map((para, idx) => (
                    <p key={idx} className="text-slate-600 leading-relaxed mb-6">
                      {para}
                    </p>
                  ))}
                </>
              ) : (
                <>
                  <h2 className="text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
                    Improving Lives Through Employment
                  </h2>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    We actively engage with employers, local businesses, and community organizations to create a robust network of opportunities.
                  </p>
                </>
              )}
            </div>
            <div className="flex items-stretch">
              <img 
                src={content?.hero_image || '/assets/vvvsi-images/about-hero.jpg'}
                alt="VVVSI Team"
                className="rounded-xl shadow-lg w-full h-full object-cover min-h-[400px]"
              />
            </div>
          </div>

          {/* How It Works - Features */}
          {content?.features && content.features.length > 0 && (
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center" style={{ fontFamily: 'Outfit' }}>
                How It Works
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {content.features.map((feature, index) => (
                  <Card key={index} className="p-6 text-center border-slate-200 hover:shadow-lg transition-shadow">
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-teal-600">{String(index + 1).padStart(2, '0')}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">{feature.title}</h3>
                    <p className="text-slate-600 text-sm">
                      {feature.description}
                    </p>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Stats Section */}
          <div className="bg-slate-900 rounded-2xl p-8 mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center" style={{ fontFamily: 'Outfit' }}>
              Our Achievements
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <stat.icon className="w-6 h-6 text-teal-400" />
                  </div>
                  <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
                  <p className="text-sm text-slate-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Associations */}
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center" style={{ fontFamily: 'Outfit' }}>
              Government & Industry Associations
            </h2>
            <Card className="p-8 border-slate-200">
              <div className="grid lg:grid-cols-2 gap-8 items-stretch">
                <div className="flex flex-col justify-center">
                  <p className="text-slate-600 mb-6">
                    {content?.sections?.[1]?.content?.split('\n')[0] || 'Vallalar Velai Vaaipu Sevai Iyakkam – Trust, Social Service Associated with Below Government Industry & HR Bodies:'}
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {defaultAssociations.map((assoc, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                        <div className="w-2 h-2 bg-teal-500 rounded-full flex-shrink-0" />
                        <span className="text-sm text-slate-700">{assoc}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-stretch">
                  <img 
                    src={content?.gallery_images?.[1] || '/assets/vvvsi-images/about-team.jpg'}
                    alt="VVVSI Team"
                    className="rounded-xl shadow-lg w-full h-full object-cover min-h-[300px]"
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default About;
